import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useGetVehicleDetails from '../../../api/vehicles/useGetVehicleDetails';
import usePutVehicle from '../../../api/vehicles/usePutVehicle';
import useGetFactories from '../../../api/factories/useGetFactories';
import { VEHICLE_COLORS } from '../../../pages/Vehicles/components/vehicle-color-picker';
import { VEHICLE_TYPES } from '../../../pages/Vehicles/components/vehicle-type-radio';
import { VcField } from '../CreateVehicleModal/steps/vehicle-form-fields';

const DEFAULT_COLOR = '#3B82F6';

// Pause ajoutée par défaut : 30 min autour de midi
const NEW_BREAK = {
  time_window_start: '12:00',
  time_window_end: '14:00',
  service: 1800,
  description: 'Pause déjeuner',
};

const numberFormatter = new Intl.NumberFormat('fr-FR');

// Les heures arrivent de l'API au format HH:mm:ss, les champs time veulent HH:mm
const toTimeInput = (value) => (value ? String(value).slice(0, 5) : '');

const isEmpty = (value) =>
  value === '' || value === null || value === undefined;

const toNumberOrUndefined = (value, parse = parseFloat) =>
  isEmpty(value) ? undefined : parse(value);

// Champ facultatif vidé dans la modale : null l'efface en base (undefined
// laisserait l'ancienne valeur)
const toNumberOrNull = (value) =>
  isEmpty(value) ? null : Math.round(parseFloat(value) * 100) / 100;

const toFormData = (management) => ({
  vehicule_key: management.vehicule_key || '',
  label: management.label || '',
  imat: management.imat || '',
  phone_number: management.phone_number || '',
  vehicle_type: management.vehicle_type || '',
  color: management.color || DEFAULT_COLOR,
  factory_id: management.factory_id || '',
  total_capacity: management.total_capacity ?? '',
  linear_capacity: management.linear_capacity ?? '',
  containers: management.containers || [],
  time_window_start: toTimeInput(management.time_window_start),
  time_window_end: toTimeInput(management.time_window_end),
  // Formulaire en heures et km, API en secondes et mètres
  max_travel_time: management.max_travel_time
    ? String(management.max_travel_time / 3600)
    : '',
  max_distance: management.max_distance
    ? String(management.max_distance / 1000)
    : '',
  costs_per_km: management.costs_per_km ?? '',
  speed_factor: management.speed_factor ?? '',
  breaks: (management.breaks || []).map((item) => ({
    ...item,
    time_window_start: toTimeInput(item.time_window_start),
    time_window_end: toTimeInput(item.time_window_end),
  })),
});

/**
 * Capacité d'un compartiment ramenée en kg (le litre compte pour 1 kg)
 */
const toKg = (container) => {
  const capacity = parseFloat(container.capacity) || 0;
  return container.capacity_unit === 'TONNE' ? capacity * 1000 : capacity;
};

/**
 * Onglet « Détails » de la modale d'édition : mêmes sections et champs que
 * la modale de création (redesign v4), plus les paramètres d'optimisation.
 * Les compétences VROOM ne sont plus éditées ici : aucune étape n'en porte,
 * elles restaient sans effet. Les valeurs existantes ne sont pas touchées.
 */
const DetailsPanel = forwardRef(
  ({ vehicleId, vehicleKey, onSavingChange }, ref) => {
    const { data, isLoading, refetch } = useGetVehicleDetails({ vehicleId });
    const { isLoading: isSaving, putVehicle } = usePutVehicle();
    const { factories, isLoading: isLoadingFactories } = useGetFactories();
    const [form, setForm] = useState(null);

    useEffect(() => {
      if (data?.management) setForm(toFormData(data.management));
    }, [data]);

    useEffect(() => {
      if (onSavingChange) onSavingChange(isSaving);
    }, [isSaving, onSavingChange]);

    const handleChange = (field, value) =>
      setForm((prev) => ({ ...prev, [field]: value }));

    const handleContainerChange = (index, field, value) =>
      setForm((prev) => ({
        ...prev,
        containers: prev.containers.map((container, i) =>
          i === index ? { ...container, [field]: value } : container
        ),
      }));

    const handleBreakChange = (index, field, value) =>
      setForm((prev) => ({
        ...prev,
        breaks: prev.breaks.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      }));

    const hasContainers = form?.containers?.length > 0;
    // Avec des compartiments, la capacité totale en est la somme
    const totalCapacity = hasContainers
      ? form.containers.reduce((sum, container) => sum + toKg(container), 0)
      : form?.total_capacity;

    /**
     * Enregistre le véhicule ; renvoie true si l'API a accepté
     */
    const save = async () => {
      if (!form) return false;
      try {
        await putVehicle(vehicleKey, {
          label: form.label,
          imat: form.imat,
          phone_number: form.phone_number,
          vehicle_type: form.vehicle_type,
          color: form.color,
          factory_id: toNumberOrUndefined(form.factory_id, (v) =>
            parseInt(v, 10)
          ),
          total_capacity: toNumberOrUndefined(totalCapacity),
          linear_capacity: toNumberOrNull(form.linear_capacity),
          containers: form.containers.map((container) => ({
            id: container.id,
            container_key: container.container_key,
            capacity: parseFloat(container.capacity),
            capacity_unit: container.capacity_unit,
          })),
          costs_per_km: toNumberOrNull(form.costs_per_km),
          speed_factor: toNumberOrNull(form.speed_factor),
          // Saisie en heures et km, API en secondes et mètres (entiers)
          max_travel_time: isEmpty(form.max_travel_time)
            ? null
            : Math.round(parseFloat(form.max_travel_time) * 3600),
          max_distance: isEmpty(form.max_distance)
            ? null
            : Math.round(parseFloat(form.max_distance) * 1000),
          time_window_start: form.time_window_start || null,
          time_window_end: form.time_window_end || null,
          breaks: form.breaks.map((item) => ({
            time_window_start: item.time_window_start,
            time_window_end: item.time_window_end,
            service: parseInt(item.service, 10) || 0,
            description: item.description,
          })),
        });
        refetch();
        return true;
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du véhicule:', error);
        return false;
      }
    };

    useImperativeHandle(ref, () => ({ save, isSaving }));

    if (isLoading || !form) {
      return (
        <div className="vd">
          {[0, 1, 2].map((index) => (
            <SkeletonLoader
              key={index}
              width="100%"
              height={120}
              style={{ borderRadius: 8 }}
            />
          ))}
        </div>
      );
    }

    const selectedColor = VEHICLE_COLORS.find(
      (color) => color.value === form.color
    );

    return (
      <div className="vd">
        <section className="vd-section">
          <div className="vd-section__head">
            <div>
              <h3 className="vd-section__title">Identification</h3>
              <p className="vd-section__description">
                Ce qui permet de reconnaître le véhicule et de joindre le
                chauffeur.
              </p>
            </div>
          </div>
          <div className="vd-grid">
            <VcField
              label="Numéro du camion"
              hint="Identifiant, non modifiable"
            >
              <input className="vd-input" value={form.vehicule_key} disabled />
            </VcField>
            <VcField label="Libellé">
              <input
                className="vd-input"
                value={form.label}
                onChange={(e) => handleChange('label', e.target.value)}
                placeholder="Ex : Camion frigorifique"
              />
            </VcField>
            <VcField label="Immatriculation">
              <input
                className="vd-input vd-input--upper"
                value={form.imat}
                onChange={(e) =>
                  handleChange('imat', e.target.value.toUpperCase())
                }
                placeholder="AA-123-BB"
              />
            </VcField>
            <VcField label="Téléphone du camion">
              <input
                className="vd-input"
                type="tel"
                value={form.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                placeholder="06 12 34 56 78"
              />
            </VcField>
          </div>
        </section>

        <section className="vd-section">
          <div className="vd-section__head">
            <div>
              <h3 className="vd-section__title">Exploitation</h3>
              <p className="vd-section__description">
                Le site de rattachement sert de point de départ des tournées.
              </p>
            </div>
          </div>
          <div className="vd-grid">
            <VcField label="Site de rattachement">
              <select
                className="vd-input"
                value={form.factory_id || ''}
                onChange={(e) => handleChange('factory_id', e.target.value)}
                disabled={isLoadingFactories}
              >
                <option value="">Aucun site</option>
                {Array.isArray(factories) &&
                  factories.map((factory) => (
                    <option key={factory.id} value={factory.id}>
                      {factory.name}
                    </option>
                  ))}
              </select>
            </VcField>
          </div>
          <div className="vd-field vd-field--wide">
            <span className="vd-field__label">Type de véhicule</span>
            <div className="vd-chips" role="radiogroup">
              {VEHICLE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  role="radio"
                  aria-checked={form.vehicle_type === type.value}
                  className={`vd-chip ${
                    form.vehicle_type === type.value ? 'vd-chip--active' : ''
                  }`}
                  onClick={() => handleChange('vehicle_type', type.value)}
                >
                  <FontAwesomeIcon icon={type.icon} />
                  {type.value}
                </button>
              ))}
            </div>
          </div>
          <div className="vd-field vd-field--wide">
            <span className="vd-field__label">
              Couleur sur la carte
              <span className="vd-field__inline">
                {selectedColor?.label || form.color}
              </span>
            </span>
            <div className="vd-swatches" role="radiogroup">
              {VEHICLE_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  role="radio"
                  aria-checked={form.color === color.value}
                  aria-label={color.label}
                  title={color.label}
                  className={`vd-swatch ${
                    form.color === color.value ? 'vd-swatch--active' : ''
                  }`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => handleChange('color', color.value)}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="vd-section">
          <div className="vd-section__head">
            <div>
              <h3 className="vd-section__title">Capacité</h3>
              <p className="vd-section__description">
                Utilisée par l&apos;optimisation pour ne jamais surcharger le
                véhicule.
              </p>
            </div>
          </div>
          <div className="vd-grid">
            <VcField
              label="Capacité totale"
              suffix="kg"
              hint={hasContainers ? 'Somme des compartiments' : undefined}
            >
              <input
                className="vd-input"
                type="number"
                min="0"
                value={totalCapacity}
                onChange={(e) => handleChange('total_capacity', e.target.value)}
                disabled={hasContainers}
                placeholder="1800"
              />
            </VcField>
            <VcField label="Mètres linéaires" suffix="m">
              <input
                className="vd-input"
                type="number"
                min="0"
                step="0.1"
                value={form.linear_capacity}
                onChange={(e) =>
                  handleChange('linear_capacity', e.target.value)
                }
                placeholder="13,6"
              />
            </VcField>
          </div>
          {hasContainers && (
            <>
              <div className="vd-table">
                <div className="vd-table__row vd-table__row--head">
                  <span className="vd-col--name">Compartiment</span>
                  <span className="vd-col--capacity">Capacité</span>
                  <span className="ve-col--unit">Unité</span>
                </div>
                {form.containers.map((container, index) => (
                  <div key={container.id || index} className="vd-table__row">
                    <span className="vd-col--name">
                      {container.container_key || `Compartiment ${index + 1}`}
                    </span>
                    <span className="vd-col--capacity">
                      <input
                        className="vd-input"
                        type="number"
                        min="0"
                        value={container.capacity}
                        onChange={(e) =>
                          handleContainerChange(
                            index,
                            'capacity',
                            e.target.value
                          )
                        }
                        aria-label={`Capacité du compartiment ${index + 1}`}
                      />
                    </span>
                    <span className="ve-col--unit">
                      <select
                        className="vd-input"
                        value={container.capacity_unit}
                        onChange={(e) =>
                          handleContainerChange(
                            index,
                            'capacity_unit',
                            e.target.value
                          )
                        }
                        aria-label={`Unité du compartiment ${index + 1}`}
                      >
                        <option value="TONNE">t</option>
                        <option value="KG">kg</option>
                        <option value="LITRE">L</option>
                      </select>
                    </span>
                  </div>
                ))}
              </div>
              <div className="vc-total">
                <span>
                  {form.containers.length} compartiment
                  {form.containers.length > 1 ? 's' : ''}
                </span>
                <span className="vc-total__value">
                  Capacité totale : {numberFormatter.format(totalCapacity)} kg
                </span>
              </div>
            </>
          )}
        </section>

        <section className="vd-section">
          <div className="vd-section__head">
            <div>
              <h3 className="vd-section__title">Horaires et optimisation</h3>
              <p className="vd-section__description">
                Départ et fin de tournée servent au calcul des heures
                d&apos;arrivée : une étape au-delà de la fin est signalée hors
                horaires. Laissez vide ce qui ne s&apos;applique pas.
              </p>
            </div>
          </div>
          <div className="vd-grid">
            <VcField label="Départ de tournée">
              <input
                className="vd-input"
                type="time"
                value={form.time_window_start}
                onChange={(e) =>
                  handleChange('time_window_start', e.target.value)
                }
              />
            </VcField>
            <VcField
              label="Fin de tournée au plus tard"
              hint="Antérieure au départ : le lendemain (tournée de nuit)"
            >
              <input
                className="vd-input"
                type="time"
                value={form.time_window_end}
                onChange={(e) =>
                  handleChange('time_window_end', e.target.value)
                }
              />
            </VcField>
            <VcField label="Temps de trajet maximum" suffix="h">
              <input
                className="vd-input"
                type="number"
                min="0"
                step="0.5"
                value={form.max_travel_time}
                onChange={(e) =>
                  handleChange('max_travel_time', e.target.value)
                }
                placeholder="8"
              />
            </VcField>
            <VcField label="Distance maximale" suffix="km">
              <input
                className="vd-input"
                type="number"
                min="0"
                value={form.max_distance}
                onChange={(e) => handleChange('max_distance', e.target.value)}
                placeholder="500"
              />
            </VcField>
            <VcField label="Coût au kilomètre" suffix="€">
              <input
                className="vd-input"
                type="number"
                min="0"
                step="0.01"
                value={form.costs_per_km}
                onChange={(e) => handleChange('costs_per_km', e.target.value)}
                placeholder="1,50"
              />
            </VcField>
            <VcField
              label="Facteur de vitesse"
              hint="1 = vitesse normale, 0,8 = 20 % plus lent"
            >
              <input
                className="vd-input"
                type="number"
                min="0.1"
                max="2"
                step="0.1"
                value={form.speed_factor}
                onChange={(e) => handleChange('speed_factor', e.target.value)}
                placeholder="1"
              />
            </VcField>
          </div>
        </section>

        <section className="vd-section">
          <div className="vd-section__head">
            <div>
              <h3 className="vd-section__title">Pauses</h3>
              <p className="vd-section__description">
                Le chauffeur prend chaque pause dans sa plage horaire. Prises en
                compte par « Optimiser les tournées ».
              </p>
            </div>
            <button
              type="button"
              className="vd-link"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  breaks: [...prev.breaks, { ...NEW_BREAK }],
                }))
              }
            >
              + Ajouter une pause
            </button>
          </div>
          {form.breaks.length > 0 ? (
            <div className="vd-table">
              <div className="vd-table__row vd-table__row--head">
                <span className="vd-col--name">Libellé</span>
                <span className="ve-col--time">Entre</span>
                <span className="ve-col--time">Et</span>
                <span className="ve-col--duration">Durée</span>
                <span className="vd-col--remove" />
              </div>
              {form.breaks.map((item, index) => (
                <div key={item.id || index} className="vd-table__row">
                  <span className="vd-col--name">
                    <input
                      className="vd-input"
                      value={item.description || ''}
                      onChange={(e) =>
                        handleBreakChange(index, 'description', e.target.value)
                      }
                      placeholder="Pause déjeuner"
                      aria-label={`Libellé de la pause ${index + 1}`}
                    />
                  </span>
                  <span className="ve-col--time">
                    <input
                      className="vd-input"
                      type="time"
                      value={item.time_window_start}
                      onChange={(e) =>
                        handleBreakChange(
                          index,
                          'time_window_start',
                          e.target.value
                        )
                      }
                      aria-label={`Début de plage de la pause ${index + 1}`}
                    />
                  </span>
                  <span className="ve-col--time">
                    <input
                      className="vd-input"
                      type="time"
                      value={item.time_window_end}
                      onChange={(e) =>
                        handleBreakChange(
                          index,
                          'time_window_end',
                          e.target.value
                        )
                      }
                      aria-label={`Fin de plage de la pause ${index + 1}`}
                    />
                  </span>
                  <span className="vd-input-wrap ve-col--duration">
                    <input
                      className="vd-input"
                      type="number"
                      min="0"
                      step="5"
                      // Saisie en minutes, stockage en secondes
                      value={item.service ? Math.round(item.service / 60) : ''}
                      onChange={(e) =>
                        handleBreakChange(
                          index,
                          'service',
                          (parseInt(e.target.value, 10) || 0) * 60
                        )
                      }
                      aria-label={`Durée de la pause ${index + 1}`}
                    />
                    <span className="vd-input-wrap__suffix">min</span>
                  </span>
                  <button
                    type="button"
                    className="vd-remove vd-col--remove"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        breaks: prev.breaks.filter((_, i) => i !== index),
                      }))
                    }
                    title="Supprimer cette pause"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="ve-empty">Aucune pause.</p>
          )}
        </section>
      </div>
    );
  }
);

DetailsPanel.displayName = 'DetailsPanel';

export default DetailsPanel;
